import type { MenuProps } from 'antd';
import { Menu } from 'antd';
import type { ComponentRef, FocusEvent, KeyboardEvent } from 'react';
import { useRef } from 'react';

import { useNavigate } from './router';
import type { NavItem } from './types';

type AntItem = NonNullable<MenuProps['items']>[number];

/**
 * Build the onClick handler for a leaf item based on its navigation target.
 * antd Menu natively handles Enter-key activation on its items, so this
 * covers the click path (mouse and Enter). Space activation is wired
 * separately in `toAntItem` via `buildOnKeyDown`, since rc-menu does not
 * handle the Space key at all.
 */
function buildOnClick(
  item: NavItem,
  navigate: ReturnType<typeof useNavigate>,
): (() => void) | undefined {
  if (item.to) return () => navigate(item.to!);
  if (item.href)
    return () => window.open(item.href, '_blank', 'noopener,noreferrer');
  if (item.onClick) return item.onClick;
  return undefined;
}

/**
 * Build a Space-only keydown handler for a leaf item. Enter is left to
 * rc-menu's own handling (it already invokes onClick on Enter); handling it
 * again here would double-fire the action.
 */
function buildOnKeyDown(
  onClick?: () => void,
): ((e: KeyboardEvent<HTMLElement>) => void) | undefined {
  if (!onClick) return undefined;
  return (e) => {
    if (e.key !== ' ') return;
    e.preventDefault();
    onClick();
  };
}

function handleMenuFocus(
  e: FocusEvent<HTMLUListElement>,
  menu: ComponentRef<typeof Menu> | null,
) {
  if (e.target === e.currentTarget) {
    menu?.focus();
  } else {
    e.currentTarget.tabIndex = -1;
  }
}

function handleMenuBlur(e: FocusEvent<HTMLUListElement>) {
  if (
    !(e.relatedTarget instanceof Node) ||
    !e.currentTarget.contains(e.relatedTarget)
  ) {
    e.currentTarget.tabIndex = 0;
  }
}

function toAntItem(
  item: NavItem,
  navigate: ReturnType<typeof useNavigate>,
): AntItem {
  const hasChildren = !!item.children?.length;
  const onClick =
    hasChildren || item.disabled ? undefined : buildOnClick(item, navigate);
  return {
    key: item.key,
    title: item.label,
    icon: item.icon,
    className: item.className,
    disabled: item.disabled,
    label: item.ariaLabel ? (
      <span aria-label={item.ariaLabel}>{item.label}</span>
    ) : (
      item.label
    ),
    onClick,
    onKeyDown: buildOnKeyDown(onClick),
    ...(hasChildren
      ? { children: item.children!.map((c) => toAntItem(c, navigate)) }
      : {}),
  } as AntItem;
}

export interface NavMenuProps {
  items: NavItem[];
  selectedKeys?: string[];
  defaultSelectedKeys?: string[];
  defaultOpenKeys?: string[];
  className?: string;
  inlineIndent?: number;
}

/**
 * Data-driven sidebar menu. Converts framework-agnostic `NavItem`s into an
 * accessible Ant Design inline menu, centralising the keyboard/aria wiring that
 * consumers previously hand-rolled per menu.
 *
 * Navigation is handled via onClick callbacks rather than nested anchor
 * elements, which avoids placing interactive `<a>` elements inside antd's
 * `<li role="menuitem">` (an ARIA violation).
 */
export function NavMenu({
  items,
  selectedKeys,
  defaultSelectedKeys,
  defaultOpenKeys,
  className,
  inlineIndent = 10,
}: Readonly<NavMenuProps>) {
  const navigate = useNavigate();
  const menuRef = useRef<ComponentRef<typeof Menu>>(null);
  return (
    <Menu
      ref={menuRef}
      // Avoid invalid aria-describedby references before tooltips exist. See AB#2620.
      _internalDisableMenuItemTitleTooltip
      mode="inline"
      onFocus={(e) => handleMenuFocus(e, menuRef.current)}
      onBlur={handleMenuBlur}
      inlineIndent={inlineIndent}
      className={className}
      selectedKeys={selectedKeys}
      defaultSelectedKeys={defaultSelectedKeys}
      defaultOpenKeys={defaultOpenKeys}
      items={items.map((i) => toAntItem(i, navigate))}
    />
  );
}
